import jwtDecode from 'jwt-decode';

// Debug-logg for sesjonshåndtering (ENK-29). Skriver til konsollet OG til
// localStorage, fordi driftsskjermen redirecter til OneLogin og tilbake —
// konsollet tømmes da med mindre «Preserve log» er huket av. Ringbufferen
// overlever redirects, så support kan hente historikken i etterkant med:
//
//   enkSesjonslogg()          → tabell i konsollet
//   enkSesjonslogg(true)      → tømmer loggen
//
// Vi logger aldri token-innhold, kun metadata (aud, exp, sub-lengde o.l.).

const LAGRINGSNOKKEL = 'enk.sesjonslogg';
const MAKS_HENDELSER = 200;
const PREFIKS = '[ENK-sesjon]';

const osloFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Oslo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const tosifret = (n: number): string => String(n).padStart(2, '0');

// Norsk lokaltid med offset, f.eks. «2026-11-02 14:27:17.954 +01:00».
// Offset tas med så tidspunktene er entydige og sorterbare over sommertid.
export const osloTid = (dato: Date = new Date()): string => {
  const deler = Object.fromEntries(
    osloFormat.formatToParts(dato).map((d) => [d.type, d.value])
  );
  const lokalSomUtc = Date.UTC(
    Number(deler.year),
    Number(deler.month) - 1,
    Number(deler.day),
    Number(deler.hour),
    Number(deler.minute),
    Number(deler.second)
  );
  const offsetMin = Math.round(
    (lokalSomUtc - (dato.getTime() - dato.getMilliseconds())) / 60_000
  );
  const fortegn = offsetMin < 0 ? '-' : '+';
  const abs = Math.abs(offsetMin);
  const ms = String(dato.getMilliseconds()).padStart(3, '0');
  return (
    `${deler.year}-${deler.month}-${deler.day} ` +
    `${deler.hour}:${deler.minute}:${deler.second}.${ms} ` +
    `${fortegn}${tosifret(Math.floor(abs / 60))}:${tosifret(abs % 60)}`
  );
};

export type Sesjonshendelse = {
  tid: string;
  hendelse: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: Record<string, any>;
};

const lesLogg = (): Sesjonshendelse[] => {
  try {
    const raa = window.localStorage.getItem(LAGRINGSNOKKEL);
    return raa ? (JSON.parse(raa) as Sesjonshendelse[]) : [];
  } catch {
    return [];
  }
};

const skrivLogg = (hendelser: Sesjonshendelse[]): void => {
  try {
    window.localStorage.setItem(
      LAGRINGSNOKKEL,
      JSON.stringify(hendelser.slice(-MAKS_HENDELSER))
    );
  } catch {
    // Full eller blokkert localStorage skal aldri velte appen.
  }
};

export const sesjonsLogg = (
  hendelse: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: Record<string, any>
): void => {
  const oppfoering: Sesjonshendelse = {
    tid: osloTid(),
    hendelse,
    data,
  };

  // eslint-disable-next-line no-console
  console.log(`${PREFIKS} ${oppfoering.tid} ${hendelse}`, data ?? '');

  const hendelser = lesLogg();
  hendelser.push(oppfoering);
  skrivLogg(hendelser);
};

// Metadata fra brukerens tokens — nok til å se om `aud` endrer seg ved
// fornyelse (hovedhypotesen i ENK-29) og om refresh token finnes, uten å
// eksponere selve tokenene.
export type TokenBaerer = {
  // eslint-disable-next-line camelcase
  access_token?: string;
  // eslint-disable-next-line camelcase
  refresh_token?: string;
};

export const tokenInfo = (
  bruker?: TokenBaerer | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> => {
  const harRefreshToken = Boolean(bruker?.refresh_token);
  const accessToken = bruker?.access_token;
  if (!accessToken) return { harToken: false, harRefreshToken };
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload = jwtDecode<any>(accessToken);
    const utloper = payload.exp ? new Date(payload.exp * 1000) : null;
    return {
      harToken: true,
      harRefreshToken,
      aud: payload.aud,
      iss: payload.iss,
      roller: payload.roles,
      utloper: utloper ? osloTid(utloper) : undefined,
      sekunderIgjen: payload.exp
        ? Math.round(payload.exp - Date.now() / 1000)
        : null,
    };
  } catch (feil) {
    return { harToken: true, harRefreshToken, dekodingFeilet: String(feil) };
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const feilInfo = (feil: unknown): Record<string, any> => {
  const kilde = feil as
    | {
        error?: string;
        // eslint-disable-next-line camelcase
        error_description?: string;
        message?: string;
        name?: string;
      }
    | null
    | undefined;
  if (!kilde) return { feil: null };
  return {
    navn: kilde.name,
    error: kilde.error,
    beskrivelse: kilde.error_description,
    melding: kilde.message,
    nettverkStatus: window.navigator.onLine ? 'online' : 'offline',
  };
};

// Gjør loggen tilgjengelig fra konsollet på driftsmaskinen.
export const registrerKonsollhjelper = (): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).enkSesjonslogg = (tom = false): Sesjonshendelse[] => {
    if (tom) {
      try {
        window.localStorage.removeItem(LAGRINGSNOKKEL);
      } catch {
        // ignorer
      }
      return [];
    }
    const hendelser = lesLogg();
    // eslint-disable-next-line no-console
    console.table(
      hendelser.map((h) => ({
        tid: h.tid,
        hendelse: h.hendelse,
        data: JSON.stringify(h.data ?? {}),
      }))
    );
    return hendelser;
  };
};

export const skrivOppstartslogg = (): void => {
  registrerKonsollhjelper();
  sesjonsLogg('oppstart', {
    url: window.location.href,
    harAuthParams: /[?&](code|error|state)=/.test(window.location.search),
    online: window.navigator.onLine,
    antallTidligereHendelser: lesLogg().length,
  });
  // eslint-disable-next-line no-console
  console.log(
    `${PREFIKS} historikk er tilgjengelig i konsollet med enkSesjonslogg()`
  );
};

export default sesjonsLogg;
